import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FilterEmptyState } from '@/components/ui/custom/FilterEmptyState';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { InsightFilter, InsightOperator } from '@/shared/types/analytics';
import { Insight } from '@/shared/types/insights';

import { DEFAULT_OPERATORS, OPERATOR_LABELS } from './constants';
import { FilterCheckboxItem } from './FilterCheckboxItem';

interface CategoricalInsightFilterProps {
  filterIndex: number;
  insight: Insight;
}

export const CategoricalInsightFilter = ({
  filterIndex,
  insight,
}: CategoricalInsightFilterProps) => {
  const { t } = useTranslation();
  const { control, getValues } = useFormContext();

  const { update } = useFieldArray({ control, name: 'filters' });

  // Get the insightFilters array
  const insightFilters = useWatch({ control, name: `filters.${filterIndex}.insightFilters` }) || [];

  // Check if this insight already exists in the filters
  const existingFilterIndex = insightFilters.findIndex(
    (filter: InsightFilter) => filter.id === insight.name
  );

  const currentFilter =
    existingFilterIndex !== -1
      ? insightFilters[existingFilterIndex]
      : {
          id: insight.name,
          category: insight.type,
          operator: DEFAULT_OPERATORS[insight.type] || 'in',
          values: [],
        };

  const currentValues = currentFilter.values || [];

  const updateFilter = (newInsightFilter: InsightFilter) => {
    const updatedFilters = [...insightFilters];

    if (existingFilterIndex !== -1) {
      // Update existing filter
      updatedFilters[existingFilterIndex] = newInsightFilter;
    } else {
      // Add new filter
      updatedFilters.push(newInsightFilter);
    }

    const currentFormFilter = getValues(`filters.${filterIndex}`);
    update(filterIndex, { ...currentFormFilter, insightFilters: updatedFilters });
  };

  const handleOperatorChange = (newOperator: InsightOperator) => {
    // Reset values when operator changes - always empty for 'exists' operator
    updateFilter({ ...currentFilter, operator: newOperator, values: [] });
  };

  const handleCategoryToggle = (category: string, checked: boolean) => {
    const newValues = checked
      ? [...currentValues, category]
      : currentValues.filter((v: string) => v !== category);

    if (newValues.length === 0) {
      // Remove the filter entirely if no values are selected
      if (existingFilterIndex !== -1) {
        const updatedFilters = [...insightFilters];
        updatedFilters.splice(existingFilterIndex, 1);
        const currentFormFilter = getValues(`filters.${filterIndex}`);
        update(filterIndex, { ...currentFormFilter, insightFilters: updatedFilters });
      }
    } else {
      // Create or update the filter with new values
      const newInsightFilter: InsightFilter = {
        id: insight.name,
        category: insight.type,
        operator: currentFilter.operator,
        values: newValues,
      };

      updateFilter(newInsightFilter);
    }
  };

  const operatorOptions = OPERATOR_LABELS[insight.type] || {};
  const isExistsOperator = currentFilter.operator === 'exists';

  // Handle empty or undefined categories
  if (!insight.categories || insight.categories.length === 0) {
    return (
      <div className="space-y-3">
        <div role="presentation" onMouseDown={(e) => e.stopPropagation()}>
          <Select value={currentFilter.operator} onValueChange={handleOperatorChange}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(operatorOptions).map(([op, label]) => (
                <SelectItem key={op} value={op}>
                  {t(label as string)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <FilterEmptyState text={t('analytics.filters.no_categories')} />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div role="presentation" onMouseDown={(e) => e.stopPropagation()}>
        <Select value={currentFilter.operator} onValueChange={handleOperatorChange}>
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(operatorOptions).map(([op, label]) => (
              <SelectItem key={op} value={op}>
                {t(label as string)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {!isExistsOperator ? (
        <div>
          {insight.categories.map((category) => {
            const isChecked = currentValues.includes(category);

            return (
              <FilterCheckboxItem
                key={category}
                id={`${insight.insight_id}-${category}`}
                checked={isChecked}
                onCheckedChange={(checked) => handleCategoryToggle(category, checked as boolean)}
              >
                {category}
              </FilterCheckboxItem>
            );
          })}
        </div>
      ) : (
        <div className="rounded-md border border-border bg-subtle p-2 text-sm text-muted-foreground">
          {t('filters.exists_description', 'Any non-null value')}
        </div>
      )}
    </div>
  );
};
